import { useMemo, useRef, useState } from 'react';

import { cx } from 'cva';
import {
  type Key,
  Select,
  type SelectProps,
  SelectValue,
  type ValidationResult,
} from 'react-aria-components/Select';

import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';

import { Button } from '../button/button';
import { FieldDescription } from '../form/field_description';
import { FieldError } from '../form/field_error';
import { FieldLabel } from '../form/field_label';
import { Icon } from '../icon/icon';
import { DropdownList } from '../list_box/dropdown_list';
import {
  SearchableDropdownList,
  type SearchableDropdownListProps,
} from '../list_box/searchable_dropdown_list';
import { Tag, TagGroup } from '../tag_group/tag_group';
import { Text } from '../text/text';
import { Tooltip } from '../tooltip/tooltip';
import { TooltipTrigger } from '../tooltip_trigger/tooltip_trigger';

import styles from './tag_field.module.css';

export const CREATE_NEW_ID = '__create_new__';

export type TagFieldOption = {
  id: string;
  label: string;
  description?: string;
};

export type TagFieldProps = Omit<
  SelectProps<TagFieldOption, 'multiple'>,
  'selectionMode'
> & {
  label?: string;
  isReadOnly?: boolean;
  isRequired?: boolean;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  options: TagFieldOption[];
  onCreateOption?: (label: string) => void;
  selectedOptionsLabel?: string;
  emptySelectionText?: string;
  editButtonLabel?: string;
  searchFieldLabel?: string;
  optionsListLabel?: string;
  noResultsText?: string;
  loadingText?: string;
  readOnlyEmptyText?: string;
  getCreateOptionText?: (value: string) => string;
  isLoading?: boolean;
  onLoadMore?: () => void;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  filter?: SearchableDropdownListProps['filter'];
  isSearchable?: boolean;
};

/**
 * TagField is a multi-select field rendered as removable tags with an
 * autocomplete list in a popover.
 *
 * Uses React Aria `Select`, `SelectValue`, and `Autocomplete` under the hood.
 *
 * See https://react-spectrum.adobe.com/react-aria/Select.html and
 * https://react-spectrum.adobe.com/react-aria/Autocomplete.html for the
 * underlying implementations.
 */
export function TagField(props: TagFieldProps) {
  const {
    errorMessage,
    description,
    options,
    onCreateOption,
    isReadOnly,
    isDisabled,
    isRequired,
    label,
    selectedOptionsLabel = 'Selected tags',
    emptySelectionText = 'Add tags',
    editButtonLabel = 'Change or add tags',
    searchFieldLabel = 'Search tags',
    optionsListLabel = 'Tag options',
    noResultsText = 'No tags found',
    loadingText = 'Loading tags…',
    readOnlyEmptyText = 'No tags',
    getCreateOptionText = (value) => `Create "${value}"`,
    onOpenChange,
    isLoading,
    onLoadMore,
    searchValue: controlledSearchValue,
    onSearchValueChange,
    filter,
    isSearchable = true,
    ...rest
  } = props;
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const isCreatable = Boolean(onCreateOption);

  const [localSearchValue, setLocalSearchValue] = useState('');
  const trimmedSearchValue = (controlledSearchValue ?? localSearchValue).trim();
  const listBoxOptions = useMemo(() => {
    if (!isCreatable) {
      return options;
    }

    if (!trimmedSearchValue) {
      return options;
    }

    const hasExactLabelMatch = options.some((option) => {
      return (
        option.label.localeCompare(trimmedSearchValue, undefined, {
          sensitivity: 'base',
        }) === 0
      );
    });

    if (hasExactLabelMatch) {
      return options;
    }

    return [
      ...options,
      {
        id: CREATE_NEW_ID,
        label: getCreateOptionText(trimmedSearchValue),
      },
    ];
  }, [getCreateOptionText, isCreatable, options, trimmedSearchValue]);

  const handleChange = (selection: Key[]) => {
    if (isCreatable && selection.includes(CREATE_NEW_ID)) {
      onCreateOption?.(trimmedSearchValue);
      setLocalSearchValue('');
      onSearchValueChange?.('');
      return;
    }

    updateSelection(selection);
  };

  const updateSelection = (selection: Key[]) => {
    rest.onChange?.(selection);
  };

  const classes = cx(styles.tag_field, rest.className);

  const listProps = {
    triggerRef,
    options: listBoxOptions,
    selectionMode: 'multiple',
    placement: 'bottom start',
    matchTriggerWidth: false,
    optionsListLabel,
    noResultsText,
    loadingText,
    isLoading,
    onLoadMore,
  } as const;

  return (
    <Select<TagFieldOption, 'multiple'>
      {...rest}
      className={classes}
      onChange={handleChange}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setLocalSearchValue('');
        }
        onOpenChange?.(isOpen);
      }}
      isRequired={isRequired}
      isDisabled={isDisabled}
      selectionMode="multiple"
      allowsEmptyCollection
    >
      {label ? <FieldLabel isRequired={isRequired}>{label}</FieldLabel> : null}
      <div ref={triggerRef} className={styles.tag_group_wrapper}>
        <SelectValue<TagFieldOption> className={styles.select_value}>
          {({ selectedItems, state }) => {
            const selectedOptions = selectedItems.filter(truthyPredicate);
            const hasSelectedItems = selectedOptions.length > 0;
            return (
              <TagGroup
                aria-label={selectedOptionsLabel}
                selectionMode="none"
                items={selectedOptions}
                onRemove={
                  isReadOnly || isDisabled
                    ? undefined
                    : (keys) => {
                        if (Array.isArray(state.value)) {
                          state.setValue(
                            state.value.filter(
                              (selectionKey) => !keys.has(selectionKey),
                            ),
                          );
                        }
                      }
                }
                renderEmptyState={() =>
                  isReadOnly ? (
                    <Text slot="description" color="low_emphasis">
                      {readOnlyEmptyText}
                    </Text>
                  ) : (
                    <Button
                      className={styles.edit_button_empty_state}
                      size="s"
                      variant="tertiary"
                      icon={<Icon name="plus" size="xs" />}
                      round
                    >
                      {emptySelectionText}
                    </Button>
                  )
                }
                trailingAction={
                  !isReadOnly && hasSelectedItems ? (
                    <TooltipTrigger>
                      <Button
                        size="s"
                        variant="tertiary"
                        icon={<Icon name="plus" />}
                        aria-label={editButtonLabel}
                        round
                        onPress={() => state.setOpen(true)}
                      />
                      <Tooltip>{editButtonLabel}</Tooltip>
                    </TooltipTrigger>
                  ) : null
                }
              >
                {(item) => <Tag>{item.label}</Tag>}
              </TagGroup>
            );
          }}
        </SelectValue>
      </div>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldError>{errorMessage}</FieldError>
      {isSearchable ? (
        <SearchableDropdownList
          {...listProps}
          searchValue={
            onSearchValueChange
              ? (controlledSearchValue ?? '')
              : localSearchValue
          }
          onSearchValueChange={onSearchValueChange ?? setLocalSearchValue}
          searchFieldLabel={searchFieldLabel}
          filter={filter}
        />
      ) : (
        <DropdownList {...listProps} />
      )}
    </Select>
  );
}
